/* eslint-disable max-lines */
import {AccountState} from './account/types';
import {EdgeRole, StreamRole, SearchRole, LakeRole} from './authz/productRolesV1';
import {OrgRole, WorkspaceRole, OrgRoles} from './authz/rolesV2';
import {ScopeTypes} from './client/types';
import {CriblFgaGetOrganizationMemberRolesResponse} from './fga/types';
import {EnvironmentType, Organization, OrganizationLicense} from './organization/types';
import {WorkspaceConfig} from './schemas/workspaceConfig';
import {WorkerGroup, WorkspaceEvent} from '../../workspace-event/src/workspaceEvent';

export type InfrastructureOperation = {
  organization: Organization;
  workspace: string;
  operationType?: InfrastructureOperationType;
};

export type InfrastructureOperationTerraform = {
  availabilityImpacted?: boolean;
  delaySeconds?: number;
  version?: string;
  planOnly?: boolean;
  terraformTarget?: string;
  preProvOnly?: boolean;
};

export type InfrastructureOperationTenantExec = DeploymentParameterTenantExec;
export type InfrastructureOperationSearchUpdate = {version: string};

export enum InfrastructureOperationType {
  SYNC = 'sync', // WorkspaceOperations.sync
}

export type LeaderInfo = {
  url: string;
  status: string;
  creationTime: number;
  authToken: string;
  password: string;
};

export type InboundPortInfo = {
  elbPort: number;
  cidr: string;
  workerGroupPort?: number;
  tls?: boolean;
  status: string;
};

export type WorkerGroupInfo = {
  name: string;
  dnsName: string;
  egressIpAddresses?: string[];
  lastUpdated?: number;
  workers: {
    min?: number;
    max?: number;
    desired?: number;
    current: number;
    instance?: any;
  };
  creationTime: number;
  status: string;
  ports?: any;
};

export type DeploymentInfo = {
  id: string;
  leader: LeaderInfo;
  workers?: WorkerGroupInfo[];
};

export interface Configuration {
  aws: AwsConfiguration;
  azure: AzureConfiguration;
  zeus: ZeusConfiguration;
  maestro: MaestroConfiguration;
  cribl_sandbox?: CriblSandboxConfiguration;
  metronome: MetronomeConfiguration;
  slack: SlackConfiguration;
  grafana: GrafanaConfiguration;
  acme_certificate: AcmeCertificateConfiguration;
  environment: EnvironmentConfiguration;
  launch_darkly: LaunchDarklyConfiguration;
  heap: HeapAnalyticsConfiguration;
  courier: CourierConfiguration;
  edge: EdgeConfiguration;
  stream: StreamConfiguration;
  segment: SegmentConfiguration;
  auth0: Auth0Configuration;
  typhon: TyphonConfiguration;
  sentry: SentryConfiguration;
  shared: SharedConfiguration;
  ddb_orgv2_settings: DDBOrgV2Settings;
  fga: FgaConfiguration;
  okta: OktaConfiguration;
  oktabackup: OktaBackupConfiguration;
  identityGovernance: IdentityGovernanceConfiguration;
  infra_service: InfraServiceConfiguration;
}

type FgaVersionKey = `v${string}`;
export type FgaConfiguration = {
  writeBatchSize?: number; // Defaults to 40 if not specified
  maxParallelCheckRequests?: number; // Uses FGA server default if not specified
  listModelsWaitTimeInMs?: number; // Defaults to 2000
  expectedCacheDuration?: number; // Defaults to 20 seconds (in ms)

  [version: FgaVersionKey]: FgaClientConfiguration;
};

/**
 * This is Okta configuration specific to Identity Governance use cases and is NOT generic.
 * @todo Refactor to be under the {@link IdentityGovernanceConfiguration}
 * @todo Refactor to use {@link OktaClientConfiguration} for {@link orgUrl}, {@link clientId}, {@link authorizationMode}
 * @deprecated
 */
export type OktaConfiguration = {
  orgUrl: string;
  awsIamAppID: string;
  groupPrefix: string;
  clientId: string;
  authorizationMode: string;
  corpOktaIdpId: string;
};

export type IdentityGovernanceConfiguration = {
  awsSSOInstanceARN: string;
  awsIdentityStoreyID: string;
  awsSSODefaultPermissionSet: IdentityGovernanceConfigurationSSOPermissionSet;
  oktaGroupOwnerID: string;
};

export type IdentityGovernanceConfigurationSSOPermissionSet = {
  arn: string;
  name: string;
};

export type FgaClientConfiguration = {
  scheme: string;
  store_id: string;
  client_id: string;
  client_secret: string;
  api_host: string;
  api_token_issuer: string;
  api_audience: string;
  auth_method: string;
};

export type AcmeCertificateConfiguration = {
  email: string;
  skipDnsValidation: boolean;
  vendors: AcmeCertificateVendorConfiguration[];
};

export type AcmeCertificateVendorConfiguration = {
  name: string;
  api_url: string;
  timeout: number;
};

export type TyphonConfiguration = {
  role_arn: string;
  audit_url: string;
  audit_port: string;
  no_touch_list_bucket: string;
  auth: AuthConfiguration;
  okta_client: TyphonOktaConfiguration;
  auth0_client: TyphonAuth0Configuration;
  auditEnabled: boolean;
};

export type CourierConfiguration = {
  enabled: boolean;
  api_key: string;
  client_key: string;
  api_url: string;
  maintenance_upcoming_nid: string;
  org_license_change_nid: string;
  org_cleanup_nid: string;
  customer_delete_org_ack_nid: string;
};

export type StreamConfiguration = {
  leader_api_url: string;
};

export type EdgeConfiguration = {
  otel_metrics_endpoint: string;
  otel_export_interval: number;
};

export type InfraServiceConfiguration = {
  api_url: string;
};

export type DefaultConnectionInfo = {
  id: string;
  name: string;
};

export type AudienceConfiguration = {
  api: string;
  browser: string;
};

export type Auth0Configuration = {
  secret: string;
  sessionTokenSecret: string;
};

export type OIDCClientConfiguration = {
  client_id: string;
  audiences?: AudienceConfiguration;
  issuer?: string;
  jwks_uri?: string;
};

export type OIDCDeviceFlowClientConfiguration = {
  device_authorization_endpoint: string;
  token_endpoint: string;
};

/**
 * This is configuration used to set up the Okta SDK {@link Okta.Client} to use the Okta Management APIs.
 * It should ONLY contain configuration specific to this purpose.
 */
export type OktaClientConfiguration = OIDCClientConfiguration & {
  org_url: string;
  authorization_mode: string;
  scopes?: string[];
  secret_id: string;
};

export type Auth0ClientConfiguration = OIDCClientConfiguration & {
  domain: string;
  default_client_token_ttl: number;
};

/**
 * Extends {@link OktaClientConfiguration} with Zeus specific configuration
 * Extends {@link Auth0ClientConfiguration} with Typhon specific configuration
 * It's unnecessary but having these blank types here can help prove when
 * we're not using any IDP specific features anymore.
 * e.g: Autho still depends on default connection IDs.
 */
export type ZeusOktaConfiguration = OktaClientConfiguration;
export type ZeusAuth0Configuration = Auth0ClientConfiguration & {
  default_connection_ids?: DefaultConnectionInfo[];
  max_retries: number;
};
export type TyphonOktaConfiguration = OktaClientConfiguration & OIDCDeviceFlowClientConfiguration;
export type TyphonAuth0Configuration = Auth0ClientConfiguration & OIDCDeviceFlowClientConfiguration;

export type MaestroAuthConfiguration = {
  provider: 'auth0' | 'okta';
  scope: string;
  tenant_name: string;
  domain: string;
  client_id: string;
  issuer: string;
  audiences: AudienceConfiguration;
  authorize_timeout_in_seconds: number;
  redirect_uri: string;
  unified_redirect_uri: string;
  logout_uri: string;
  custom_claim: string;
  token_endpoint?: string;
};

export type MaestroCriblAIConfiguration = {
  script_url: string;
  api_url: string;
};

export type SegmentConfiguration = {
  enabled: boolean;
  source_key: string;
  ui_source_key: string;
};

export type SentryConfiguration = {
  sample_rate: number;
};

export type SharedConfiguration = {
  domain: string;
  // Local environments will not have internal SSO setup but "real" environments all will
  internal_sso?: InternalSsoConfiguration;
};

export type InternalSsoConfiguration = {
  cribl_corp_connection: CriblCorpConnectionConfiguration;
};

export type CriblCorpConnectionConfiguration = {
  id: string;
  name: string;
};

export type HeapAnalyticsConfiguration = {
  app_id: string;
};

export interface AwsConfiguration {
  region: string;
  default_api_region: string;
  endpoint?: string;
  endpoint_asg?: string;
  endpoint_ecs?: string;
  endpoint_sts?: string;
  endpoint_marketplace_entitlement?: string;
  endpoint_marketplace_metering?: string;
  account_id: string;
  s3ForcePathStyle?: boolean;
  signatureVersion: string;
  computeChecksums: boolean;
}

export interface AzureConfiguration {
  awsSecretName: string;
  billingAccountId: string;
  billingProfileId: string;
  billingInvoiceSectionId: string;
  managementGroupId: string;
}

export interface AccountProvisioningConfiguration {
  default_license: OrganizationLicense;
  environment_type: EnvironmentType;
  supported_regions: string[];
  tenant_terraform: TenantTerraformConfiguration;
  default_minimum_assignable: number;
  region_minimum_assignable: Record<string, number>;
  initial_delay: number;
  stack_instance_delay: number;
  initial_product_build_subscription: ProductBuildSubscriptionConfiguration;
}

export interface ProductBuildSubscriptionConfiguration {
  channel: string;
  version: string;
}

export type AllowedReleaseChannels = {
  [key: string]: {
    allowed_build_qualities: string[];
  };
};
export interface TenantTerraformConfiguration {
  validate_ecr_images: boolean;
  default_provisioning_pct: number; // 0 - 100. 0 = no terraform, 100 = all terraform
}

export interface CleaningConfiguration {
  transient_test_users: string[];
  allow_enterprise: boolean;
  failure_retry_delay: number;
  requires_notification: boolean;
}

export type DefaultWorkspaceTopologyConfiguration = Pick<WorkspaceConfig,
  | 'leaderSize' |
  'enableLeaderHA' |
  'elbLeaderSingleZoneEnabled' |
  'elbWorkerSingleZoneEnabled' |
  'leaderIngressType' |
  'workerIngressType' |
  'efsThroughputMode' |
  'efsThroughputInMibps'
>;

export type SystemClientServiceConfiguration = {
  /**
   * The name of the service the system client credentials are for.
   */
  name: string;
  /**
   * The org role set in the token claim {@link AUTH0_ROLE_CLAIM} in v1 and v2 APIs with RBAC-based AuthZ checks.
   * Defaults to {@link OrgRole.USER}.
   * @deprecated This will be replaced when AuthZ for client credentials is revamped with the FGA v2 model.
   */
  role?: OrgRole;
  /**
   * The Auth0 scope/permissions to grant the system client credentials access to v2 APIs.
   * Defaults to no permissions.
   * @deprecated This will be replaced when AuthZ for client credentials is revamped with the FGA v2 model.
   */
  scope?: ScopeTypes[];
  /**
   * The FGA workspace role to grant the system client credentials access to v2 APIs.
   */
  workspaceRole?: WorkspaceRole;
};

export type SystemClientConfiguration = {
  enabled: boolean;
  services: SystemClientServiceConfiguration[];
};

export type EmailNotificationsConfiguration = {
  product_name: string;
  enabled: boolean;
  domain: string;
  sub_user_name_prefix: string;
  ip_addresses: string[];
  smtp_config_events_queue_url: string;
};

export type AuthConfiguration = {
  provider: 'auth0' | 'okta';
  grant_automatic_developer_role?: boolean;
};

export type MaestroCustomAnalytics = {
  enabled: boolean;
};

export type MaestroFullStoryAnalytics = {
  enabled: boolean;
};

export type MaestroGoogleAnalyticsConfiguration = {
  enabled: boolean;
  id: string;
};

export interface ZeusConfiguration {
  ui_url: string;
  unified_url: string;
  api_url: string;

  auth_logs_queue_url?: string;
  auth_logs_dead_letter_queue_url?: string;
  auth_logs_batch_size?: number;
  arn_org_role: string;
  arn_marketplace_role: string;
  domain: string;
  account_email_address: string;
  tenant_bucket: string;
  tenant_exec_operations_bucket: string;
  maestro_image: string;
  hosted_zone_id: string;
  marketplace_entitlement_queue_url: string;
  marketplace_metering_queue_url: string;
  marketplace_queue_batch_size: number;
  workspace_events_topic_arn: string;
  workspace_events_queue_url: string;
  workspace_events_queue_arn: string;
  workspace_events_queue_batch_size: number;
  workspace_cleanup_task_role_arn: string;
  event_bus_queue_url: string;
  dead_letter_queue_url: string;
  organization_event_bus_queue_url: string;
  organization_dead_letter_queue_url: string;
  organization_messenger_batch_size: number;
  sandbox_events_queue_url: string;
  elasticache_url: string;
  terraform_role_arn: string;
  tenant_role_arn: string;
  account_provisioning: AccountProvisioningConfiguration;
  cleaning: CleaningConfiguration;
  cleanup_api_url: string;
  test_orgs_cleanup_api_url: string;
  infrastructure: InfrastructureConfig;
  tenantTerraformDataBucket: string;
  // See https://taktak.atlassian.net/wiki/spaces/ENG/pages/1779499247/Account+state+machine+and+pools
  validate_ous: boolean;
  org_ou_topenv: string;
  org_ou_cleanup: string;

  // zeus ECS network configurations
  vpc_id: string;
  subnets: string[];
  security_groups: string[];
  load_default_workspace_config_from_file: boolean;

  default_workspace_topology: {
    standard: DefaultWorkspaceTopologyConfiguration;
    enterprise: DefaultWorkspaceTopologyConfiguration;
  };

  system_clients: SystemClientConfiguration;

  email_notifications: EmailNotificationsConfiguration;

  test_mode: boolean;
  auth: AuthConfiguration;
  okta_client: ZeusOktaConfiguration;
  auth0_client: ZeusAuth0Configuration;
  max_default_workspaces_owned: number;
  cors_origin_allow_port: boolean;
  allowed_release_channels?: AllowedReleaseChannels;
}

export interface MaestroConfiguration {
  environment: 'production' | 'staging' | 'development';
  enable_advanced_developer_tools: boolean;
  require_tos_acceptance: boolean;
  profile_column_variant: 'product_info' | 'trial';
  enable_sourcemaps: boolean;
  domain: string;
  tenant_id: string;
  suffix: string;
  // open up tunnel as a buffer between client-side and ops stream
  tunnel_url: string;
  ssa_link: string;
  port?: number;
  custom_analytics: MaestroCustomAnalytics;
  fullstory_analytics: MaestroFullStoryAnalytics;
  google_analytics: MaestroGoogleAnalyticsConfiguration;
  cribl_ai: MaestroCriblAIConfiguration;
  auth_client: MaestroAuthConfiguration;
}

export interface CriblSandboxConfiguration {
  enabled: boolean;
  domain: string;
}

export type MetricTypeIds = Record<string, string>;
export type PlanIds = {
  crowdStreamPlanId: string;
};
export type CreditTypeIds = {
  USD: string;
  criblCredits: string;
};

export interface MetronomeConfiguration {
  endpoint: string;
  metricTypeIds: MetricTypeIds;
  planIds: PlanIds;
  creditTypeIds: CreditTypeIds;
}

export interface SlackConfiguration {
  upgrade_requests_channel: string;
  token: string;
  channel: string;
  aws_marketplace_channel: string;
  slack_api_url: string;
}

export interface GrafanaConfiguration {
  api_url: string;
  token: string;
}

export interface LaunchDarklyConfiguration {
  ld_client_side_id: string;
  ld_api_url: string;
  server_side: any; // (This is really LDOptions, but we don't want the dependency here);
  ui_side?: {
    ld_base_url?: string;
    ld_events_url?: string;
    ld_stream_url?: string;
  };
  environment: string;
  ld_sdk_key: string;
  project_key: string;
}

export interface EnvironmentConfiguration {
  // TODO: [SAAS-144] Cleanup duplicate configurations that were intro'd on parallel branches with different names
  TENANT_ID: string;
  ACCOUNT_ID: string;
  LOGSTREAM_LOGIN_URL: string;
  LOG_LEVEL: string;
  JWT_ISSUER: string;
  NODE_ENV?: string;
  NODE_TLS_REJECT_UNAUTHORIZED?: string;
  TLS_DIR?: string;
}

export enum InfrastructureKind {
  Terraform = 'terraform',
  CloudwatchExporter = 'cloudwatch-exporter',
}

export type InfrastructureVersion = string;

export interface InfrastructureConfig {
  kind: InfrastructureKind;
  version: InfrastructureVersion;
  change?: InfraChange;
}

export interface DeploymentParameterSearch {
  version: string;
}

export type FilterOptions = 'leaders' | 'workers' | 'leaders+workers' | '';

export interface DeploymentParameterTenantExec {
  s3path: string;
  filters: FilterOptions;
  extravars?: string;
  shellvars?: string[];
}

export type InfraChange = {
  resourceKind: WorkspaceEvent['resource']['kind'];
  resourceSubKind?: WorkerGroup['kind'];
  resourceWorkspace: string;
  resourceName: string;
  resourceAction: WorkspaceEvent['resource']['action'];
};

/** **********************************************************************************************************
 * Notification List Roles
 *
 * This is added to handle additional requirements of sending notifications. Creating lists is based on
 * organization roles, but we also need more flexibility and hence the AuxiliryListRole enum.
 *
 */
export type NotificationListRole = AuxiliaryListRole | OrgRole;

export enum AuxiliaryListRole {
  EVERYONE = 'org_everyone',
}

export enum Product {
  EDGE = 'edge',
  SEARCH = 'search',
  STREAM = 'stream',
  LAKE = 'lake',
}

export type ProductRole = EdgeRole | StreamRole | SearchRole | LakeRole;

/**
 * This will be removed when FGA v2 migration is done. Prefer use of {@link WorkspaceProductRoles}
 * @deprecated
 */
export interface ProductRoleFull {
  workspace: string;
  productRole: ProductRole;
}

/** ********************************************************************************************************* */

export type CompactMembership = {
  tenant: string;
  organization: {id: string};
};

export type Membership = {
  organizationId: string;
  tenant: string;
  organization: Organization;
  url: string;
  roles: OrgRoles[]; // org roles for the user
  connections?: string[]; // the connections through which the user has access to the org
  /**
   * NOTE: this is a "top level" property, and not specific to any one membership.
   * True - if a user has multiple connections across all identities associated with their email
   */
  shouldSkipOnboarding?: boolean;

  /** This field will be present when a user's membership to the org is in a known-invalid state and
   *  indicates to the frontend that the user needs to go through a migration with user interaction
   *  @deprecated
   */
  invalidSsoMembership?: boolean;
};

export type Member = {
  userId: string;
  isSso: boolean;
  givenName: string;
  familyName: string;
  name?: string;
  email: string;
  status: MemberStatus;
  roles: string[];
  picture: string;
  allRoles?: CriblFgaGetOrganizationMemberRolesResponse;
  ssoGroups: string[];
  lastUpdated: Date;
};

export enum MembershipPrecedence {
  Invalid = 0,
  SocialFallback = 1,
  Default = 2,
  SocialPreferred = 3,
  TenantSSO = 4,
  CriblCorpOkta = 5,
};

export type InviteErrorAction = SignupAction | ChangeIdentityAction | ChangeUserAction |
  UnresolvableErrorAction | UnknownAction;

interface SignupAction {
  type: 'signup';
  connectionHint?: string;
};

interface ChangeIdentityAction {
  type: 'change-identity';
  connectionHint: string;
};

interface ChangeUserAction {
  type: 'change-user';
  loginHint: string;
};

interface UnresolvableErrorAction {
  type: 'unresolvable';
  message: string;
};

interface UnknownAction {
  type: 'unknown';
  originalMessage?: string;
};

export type InviteErrorActionType = InviteErrorAction['type'];

export enum MemberStatus {
  Active = 'Active',
  Deactivated = 'Deactivated',
  Unknown = 'Unknown',
}

export enum OperationState {
  Updating = 'UPDATING',
  Success = 'SUCCESS',
  Failed = 'FAILED',
  Cleaning = 'CLEANING',
}

export type ListOperationsResponse = {
  state: OperationState;
  created: string;
  updated: string;
};

export type AccountStateCount = {
  total: number;
  regions: Record<string, number>;
};

export type AccountStateCounts = {
  [key in AccountState]?: AccountStateCount;
};

type DDBOrgV2Settings = {
  create: boolean;
  update: boolean;
  throughput: 'ON_DEMAND';
  waitForActive: boolean;
};

export interface InvalidSsoUserMigrationResult {
  membershipsMigrated: number;
  createdUser?: {
    passwordReset: string;
  };
}

export interface OktaBackupConfiguration {
  bucket: string;
}
